/* eslint-disable no-unused-vars */
import { useState } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Stack,
  Box,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import useTenantMutation from '@/hooks/useTenantMutation';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ButtonGroup } from '@mui/material';
import { toast } from 'react-toastify';
import InterfaceTypeFields from './InterfaceTypeFields';

const INTERFACE_TYPES = [
  { value: 'nexmart', label: 'Nexmart' },
  { value: 'nexmart_media', label: 'Nexmart Media' },
  { value: 'weclapp', label: 'Weclapp' },
  { value: 'tanss', label: 'TANSS' },
  { value: 'tanss-erp', label: 'TANSS-ERP' },
  { value: 'vmware', label: 'VM-Ware' },
  { value: 'ftp', label: 'FTP' },
  { value: 'backup42', label: 'Acronis Backup42' },
  { value: 'host', label: 'Host' },
  { value: 'jira', label: 'Jira' },
  { value: 'acronis', label: 'Acronis' },
  { value: 'otto', label: 'Otto' },
  { value: 'paperless', label: 'Paperless' },
  { value: 'shipcloud', label: 'Shipcloud' },
  { value: 'riverbird', label: 'Riverbird' },
  { value: 'ups', label: 'UPS' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'ai', label: 'AI' },
];

const getTypeSpecificFields = (type) => {
  switch (type) {
    case 'nexmart':
      return ['headers.X-NM-API-KEY'];
    case 'weclapp':
      return ['headers.AuthenticationToken'];
    case 'tanss':
      return ['headers.apiToken', 'refreshToken'];
    case 'tanss-erp':
      return ['headers.apiToken'];
    case 'vmware':
      return ['visibleCredentials'];
    case 'ftp':
      return ['user', 'password', 'port'];
    case 'backup42':
      return ['headers.Authorization', 'username', 'password'];
    case 'host':
      return ['user', 'password'];
    case 'jira':
      return ['headers.Authorization'];
    case 'acronis':
      return ['headers.Authorization', 'username', 'password'];
    case 'otto':
      return ['headers.Authorization', 'grantType', 'scope'];
    case 'paperless':
      return ['headers.Authorization'];
    case 'shipcloud':
      return ['headers.Authorization', 'auth.username', 'auth.password'];
    case 'riverbird':
      return [
        'error',
        'password',
        'username',
        'application',
        'device',
        'body.Ticket',
      ];
    case 'ups':
      return [
        'visibleCredentials.user',
        'visibleCredentials.authProtocol',
        'visibleCredentials.authKey',
        'visibleCredentials.privProtocol',
        'visibleCredentials.privKey',
        'visibleCredentials.level',
        'visibleCredentials.port',
      ];
    case 'microsoft':
      return [
        'headers.Authorization',
        'clientId',
        'client_secret',
        'microsoftTenantId',
      ];
    case 'ai':
      return ['provider', 'modelId', 'currency'];

    default:
      return [];
  }
};

export default function InterfacesDetail({ data }) {
  const [fields, setFields] = useState(() => {
    const defaults = {
      type: '',
      description: '',
      host: '',
      path: '',
      status: data?.status || 'inactive',
      whitelist: {},
      blacklist: {},
    };

    if (data) {
      // Only include type-specific fields if they exist in the data
      const typeFields = getTypeSpecificFields(data.type);
      typeFields.forEach((field) => {
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          defaults[parent] = {
            ...defaults[parent],
            [child]: data[parent]?.[child],
          };
        } else {
          defaults[field] = data[field];
        }
      });
    }

    return { ...defaults, ...data };
  });

  const [hostError, setHostError] = useState('');

  const [pathInput, setPathInput] = useState('');
  const [selectedMethods, setSelectedMethods] = useState({
    get: false,
    post: false,
    put: false,
    delete: false,
  });
  const [listType, setListType] = useState('whitelist');
  const [showTokens, setShowTokens] = useState({
    apiToken: false,
    refreshToken: false,
    authToken: false,
    AuthenticationToken: false,
    Authorization: false,
    ['X-NM-API-KEY']: false,
  });

  const { mutateAsync: saveInterface } = useTenantMutation(
    'interface',
    data?._id,
  );

  const validateHost = (host) => {
    // Allow empty value while typing
    if (!host) return true;

    // Match hostname (e.g., example.com) or IP address
    const hostnameRegex =
      /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;

    if (hostnameRegex.test(host) || ipRegex.test(host)) {
      return true;
    }
    return false;
  };

  const handleChange = (field) => (event) => {
    let newHost = event.target.value;
    if (newHost.startsWith('http://') || newHost.startsWith('https://')) {
      newHost = newHost.replace(/^https?:\/\//, '');
    }

    if (validateHost(newHost)) {
      setHostError('');
    } else {
      setHostError(
        'Bitte geben Sie einen gültigen Hostnamen oder IP-Adresse ein',
      );
    }

    setFields((prev) => ({
      ...prev,
      [field]: newHost,
    }));
  };

  const handleRemovePath = (pathToRemove) => {
    setFields((prev) => {
      const newList = { ...prev[listType] };
      delete newList[pathToRemove];
      return {
        ...prev,
        [listType]: newList,
      };
    });
  };

  const handleAddPath = () => {
    if (!pathInput.trim()) return;

    const selectedMethodCount =
      Object.values(selectedMethods).filter(Boolean).length;

    setFields((prev) => ({
      ...prev,
      [listType]: {
        ...prev[listType],
        [pathInput]:
          selectedMethodCount === 0
            ? true
            : Object.entries(selectedMethods)
                .filter(([_, isSelected]) => isSelected)
                .reduce((acc, [method]) => {
                  acc[method] = true;
                  return acc;
                }, {}),
      },
    }));

    // Reset inputs
    setPathInput('');
    setSelectedMethods({
      get: false,
      post: false,
      put: false,
      delete: false,
    });
  };

  const handleSubmit = async () => {
    try {
      await saveInterface(fields);
    } catch (error) {
      toast.error('Failed to save interface');
    }
  };

  return (
    <Stack
      spacing={3}
      sx={{
        height: '100%',
        width: '90%',
        px: 2,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      <Typography variant="h6" gutterBottom>
        Interface Details
      </Typography>
      <Typography color="textSecondary" gutterBottom>
        {INTERFACE_TYPES.find((t) => t.value === fields.type)?.label ||
          fields.type}
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Beschreibung"
          value={fields.description}
          onChange={handleChange('description')}
        />

        <TextField
          required
          label="Host"
          value={fields.host}
          onChange={handleChange('host')}
          error={Boolean(hostError)}
          helperText={hostError}
        />

        <InterfaceTypeFields
          type={fields.type}
          fields={fields}
          setFields={setFields}
          showTokens={showTokens}
          setShowTokens={setShowTokens}
          isUpdate={false}
        />
        <FormControl fullWidth>
          <InputLabel shrink>API Endpunkte</InputLabel>
          <Box sx={{ mt: 3 }}>
            <ButtonGroup variant="contained" sx={{ mb: 2, width: '100%' }}>
              <Button
                fullWidth
                onClick={() => setListType('whitelist')}
                color={listType === 'whitelist' ? 'primary' : 'inherit'}
              >
                Whitelist
              </Button>
              <Button
                fullWidth
                onClick={() => setListType('blacklist')}
                color={listType === 'blacklist' ? 'primary' : 'inherit'}
              >
                Blacklist
              </Button>
            </ButtonGroup>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <TextField
                label="API Pfad"
                value={pathInput}
                onChange={(e) => setPathInput(e.target.value)}
                placeholder="z.B. /api/v1/users"
                sx={{
                  flexBasis: '30%',
                  width: '30%',
                  flexGrow: 0,
                  minWidth: 0,
                }}
              />
              <ToggleButtonGroup
                value={Object.entries(selectedMethods)
                  .filter(([_, value]) => value)
                  .map(([method]) => method.toUpperCase())}
                onChange={(event, newMethods) => {
                  const updated = {
                    get: false,
                    post: false,
                    put: false,
                    delete: false,
                  };
                  newMethods.forEach((method) => {
                    updated[method.toLowerCase()] = true;
                  });
                  setSelectedMethods(updated);
                }}
                aria-label="HTTP Methods"
                size="small"
                sx={{
                  flexBasis: '55%',
                  width: '55%',
                  flexGrow: 0,
                  minWidth: 0,
                  height: '56px',
                  gap: 1,
                  '& .MuiToggleButton-root': {
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 2,
                    width: 60,
                    minWidth: 60,
                    color: 'text.primary',
                    backgroundColor: 'grey.100',
                    border: '1px solid transparent',
                    transition: 'none',
                    margin: 0,
                    '&.Mui-selected': {
                      color: '#fff',
                      backgroundColor: 'primary.main',
                      border: '1px solid transparent',
                    },
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      color: '#fff',
                    },
                  },
                }}
              >
                {['GET', 'POST', 'PUT', 'DELETE'].map((method) => (
                  <ToggleButton key={method} value={method} aria-label={method}>
                    {method}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <IconButton
                onClick={handleAddPath}
                color="primary"
                sx={{
                  flexBasis: '15%',
                  width: '15%',
                  flexGrow: 0,
                  minWidth: 0,
                  height: '56px',
                }}
              >
                <AddIcon />
              </IconButton>
            </Stack>

            <Box sx={{ mt: 2 }}>
              {Object.entries(fields[listType] || {}).map(([path, methods]) => (
                <Box
                  key={path}
                  sx={{
                    mb: 1,
                    p: 1,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2">{path}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {methods === true
                        ? 'ALL METHODS'
                        : Object.keys(methods).join(', ').toUpperCase()}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePath(path)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{ mt: 2 }}
          disabled={Boolean(hostError)}
        >
          Speichern
        </Button>
      </Stack>
    </Stack>
  );
}
